import React from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { Mail, MapPin, Instagram, Facebook, Linkedin, ShieldCheck } from "lucide-react";

function FlagBox({ children, label, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-6 rounded-sm overflow-hidden shrink-0 border border-white/20">{children}</div>
      <span className="njas-condensed text-[10px] tracking-[0.22em] text-white/70 font-semibold">{label}</span>
    </div>
  );
}
function AboriginalFlag() {
  return (
    <div className="w-full h-full relative bg-black">
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-[#DE2C1C]" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#F7C000]" />
    </div>
  );
}
function TorresStraitFlag() {
  return (
    <div className="w-full h-full relative bg-[#4CAF50]">
      <div className="absolute inset-x-0 top-[16%] h-[1px] bg-white" />
      <div className="absolute inset-x-0 bottom-[16%] h-[1px] bg-white" />
      <div className="absolute inset-x-0 top-[calc(16%+1px)] bottom-[calc(16%+1px)] bg-[#0069B9]" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
        <div className="w-3 h-3 rounded-full border border-white" />
      </div>
    </div>
  );
}
function PrideFlag() {
  const rows = ["#E40303", "#FF8C00", "#FFED00", "#008026", "#004CFF", "#732982"];
  return (
    <div className="w-full h-full flex flex-col">
      {rows.map((c) => <div key={c} className="flex-1" style={{ background: c }} />)}
    </div>
  );
}

/**
 * ACNC Charity Tick placeholder.
 * The real Charity Tick is issued by the ACNC after registration and must link
 * back to the ACNC Charity Register listing. Once ACNC registration is complete,
 * swap this placeholder for the official ACNC Charity Tick artwork and update
 * ACNC_URL to point at the org's ACNC listing.
 */
const ACNC_URL = null; // TBA — set to ACNC listing URL once registered.

function AcncTick() {
  const content = (
    <div className="inline-flex items-center gap-3 border border-white/25 px-4 py-3 hover:border-white/60 transition-colors" data-testid="acnc-tick-placeholder">
      <div className="njas-badge-outline" style={{ borderColor: "#8AA083", color: "#8AA083", width: 34, height: 34 }}>
        <ShieldCheck className="w-4 h-4" />
      </div>
      <div className="leading-tight">
        <div className="njas-condensed tracking-[0.18em] font-semibold text-[10px] text-white/70">ACNC Registered Charity</div>
        <div className="njas-condensed tracking-[0.18em] font-semibold text-[11px] text-white">Charity Tick pending registration</div>
      </div>
    </div>
  );
  if (ACNC_URL) {
    return <a href={ACNC_URL} target="_blank" rel="noopener noreferrer">{content}</a>;
  }
  return content;
}

export default function Footer() {
  const loc = useLocation();
  if (loc.pathname.startsWith("/dashboard") || loc.pathname.startsWith("/login")) return null;

  return (
    <footer className="mt-24" data-testid="site-footer">
      {/* Because you are... strip */}
      <div className="bg-[var(--njas-bg-alt)] border-y border-[var(--njas-line)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14 grid md:grid-cols-2 items-center gap-6">
          <div>
            <div className="njas-script text-4xl sm:text-5xl">Because you are…</div>
            <div className="njas-display text-3xl sm:text-4xl text-njas-navy mt-1">Not Just A Statistic.</div>
          </div>
          <div className="flex md:justify-end gap-3 flex-wrap">
            <Link to="/refer" className="njas-condensed tracking-[0.2em] text-[13px] font-semibold px-5 py-3 bg-njas-navy text-njas-bg hover:bg-njas-navy-hover" data-testid="footer-refer-cta">Make a Referral</Link>
            <Link to="/get-involved" className="njas-condensed tracking-[0.2em] text-[13px] font-semibold px-5 py-3 border border-njas-navy text-njas-navy hover:bg-njas-navy hover:text-njas-bg transition-colors" data-testid="footer-involved-cta">Get Involved</Link>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="text-njas-bg/90" style={{ backgroundColor: "#2A4870" }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <Logo tone="light" markSize={52} />
              <p className="mt-8 max-w-md text-sm leading-relaxed text-njas-bg/70">
                Supporting individuals and communities through advocacy, connection and practical support.
              </p>
              <div className="mt-6 flex gap-3">
                <a href="#" aria-label="Facebook" className="njas-badge-outline !w-9 !h-9" style={{ color: "#FBF9F4", borderColor: "rgba(251,249,244,0.4)" }}><Facebook className="w-4 h-4" /></a>
                <a href="#" aria-label="Instagram" className="njas-badge-outline !w-9 !h-9" style={{ color: "#FBF9F4", borderColor: "rgba(251,249,244,0.4)" }}><Instagram className="w-4 h-4" /></a>
                <a href="#" aria-label="LinkedIn" className="njas-badge-outline !w-9 !h-9" style={{ color: "#FBF9F4", borderColor: "rgba(251,249,244,0.4)" }}><Linkedin className="w-4 h-4" /></a>
              </div>
              <div className="mt-8"><AcncTick /></div>
            </div>

            <div className="md:col-span-3">
              <div className="njas-eyebrow" style={{ color: "#BFD2B7" }}>Explore</div>
              <ul className="mt-5 space-y-3 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/services" className="hover:text-white">Services</Link></li>
                <li><Link to="/get-involved" className="hover:text-white">Get Involved</Link></li>
                <li><Link to="/refer" className="hover:text-white">Referral</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/login" className="hover:text-white">Staff Login</Link></li>
              </ul>
            </div>

            <div className="md:col-span-5">
              <div className="njas-eyebrow" style={{ color: "#BFD2B7" }}>Contact</div>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#BFD2B7" }} />
                  <a href="mailto:info@njas.org.au" className="hover:text-white transition-colors">info@njas.org.au</a>
                </li>
                <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#BFD2B7" }} /> 19/103 George Street, Parramatta NSW 2150</li>
              </ul>
              <div className="mt-6 text-xs text-njas-bg/60">
                Crisis? Call Lifeline <span className="text-white">13&nbsp;11&nbsp;14</span> or <span className="text-white">000</span> if you or someone else is in immediate danger.
              </div>
            </div>
          </div>

          {/* Acknowledgement of Country */}
          <div className="mt-14 pt-10 border-t border-white/10 grid md:grid-cols-12 gap-8 items-start" data-testid="acknowledgement-strip">
            <div className="md:col-span-8">
              <div className="njas-eyebrow" style={{ color: "#BFD2B7" }}>Acknowledgement of Country</div>
              <p className="mt-4 text-sm leading-relaxed text-njas-bg/80 max-w-2xl">
                We acknowledge the Traditional Custodians of the lands on which we work and live.
                We pay our respects to Elders past, present and emerging, and extend that respect
                to all Aboriginal and Torres Strait Islander peoples today.
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <FlagBox testid="flag-aboriginal" label="Aboriginal Flag"><AboriginalFlag /></FlagBox>
              <FlagBox testid="flag-tsi" label="Torres Strait Islander"><TorresStraitFlag /></FlagBox>
              <FlagBox testid="flag-pride" label="Progress Pride"><PrideFlag /></FlagBox>
            </div>
          </div>

          {/* Legal identity — required for Corporations Act + ACNC compliance */}
          <div className="mt-14 pt-10 border-t border-white/10" data-testid="legal-identity-block">
            <div className="grid md:grid-cols-12 gap-8">
              <div className="md:col-span-5">
                <div className="njas-eyebrow" style={{ color: "#BFD2B7" }}>Legal Identity</div>
                <div className="mt-4 njas-condensed tracking-[0.16em] text-[13px] font-semibold text-white">
                  NOT JUST A STATISTIC LTD
                </div>
                <div className="mt-2 text-xs text-njas-bg/60">
                  ABN: <span className="text-white">65 699 934 515</span>
                </div>
              </div>
              <p className="md:col-span-7 text-sm text-njas-bg/75 leading-relaxed">
                A Public Company Limited by Guarantee, incorporated in Australia. Once registration is
                complete, Not Just A Statistic Ltd will be registered as a charity with the Australian
                Charities and Not-for-profits Commission (ACNC).
              </p>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-njas-bg/50">
            <div>© {new Date().getFullYear()} Not Just A Statistic Ltd. All rights reserved.</div>
            <div className="flex gap-5">
              <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
              <span className="njas-condensed tracking-[0.22em]" style={{ color: "#BFD2B7" }}>@notjustastatistic</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
